/**
 * Iter-155B.2 — Dedicated Script Setup page.
 * Iter-155B.4 — Simplified copy + post-build completion state.
 *
 * The first-time user only needs to know two things:
 *
 *   1. Upload Script
 *   2. Build Scenes & Cast
 *
 * Until those two steps are done the page is intentionally bare.
 * Once they ARE done, the green "Once Build runs…" instructional
 * block is replaced with a calm completion card:
 *
 *   Scenes & Cast Ready · 57 scenes created · 13 cast members found
 *   [ Open Scenes ]  [ Open Cast ]
 *
 * For TV-mode projects we show the episodic setup component (which
 * already supports multi-episode upload + per-episode Build).
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft, FileText, Wand2, CheckCircle2, Loader2 } from "lucide-react";

import ProjectHeader from "../components/ProjectHeader";
import ScriptSection from "../components/ScriptSection";
import EpisodicScriptSetup from "../components/EpisodicScriptSetup";
import { api, formatApiError } from "../lib/api";
import RouteErrorBoundary from "../components/RouteErrorBoundary";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";

export default function ScriptSetupPage() {
    return (
        <RouteErrorBoundary>
            <ScriptSetupContent />
        </RouteErrorBoundary>
    );
}

function ScriptSetupContent() {
    const { projectId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    // Iter-158.13.2.20e.7 — Production scope has no dept-scope
    // downstream pages (Scenes / Cast / On Set / Fittings all live
    // under `/beta/hm-app/projects/*`). Suppress those nav CTAs on
    // Production so we don't route users into the catch-all fallback.
    const isProductionScope = apiBase === "/beta/projects";
    const [project, setProject] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    // Iter-155B.4 — track post-build completion state so the page can
    // swap its instructional copy for a results summary. Polled
    // lightly from the script slot(s) so we don't fight the user's
    // own upload component for the truth.
    const [completion, setCompletion] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`${apiBase}/${projectId}`);
                // Iter-158.13.2.20e.3 — Production Hub GET returns
                // {project, script}. H&M / legacy return flat. Unwrap
                // so `project.role`, `project.can_manage_script` etc.
                // resolve correctly under every mount.
                const flat = (data && typeof data === "object"
                    && data.project && data.project.id)
                    ? data.project
                    : data;
                if (!cancelled) setProject(flat);
            } catch (err) {
                if (!cancelled) setError(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [projectId, apiBase]);

    // Iter-155B.4 — refresh build completion state. Hits the script
    // slot for film projects, or the episodes index for TV. Used on
    // mount and every 4s while the page is open. Quiet on failure.
    const refreshCompletion = useCallback(async () => {
        if (!project) return;
        try {
            // Iter-158.13.2.20e.3 — Under the Production scope every
            // canonical script lives in `beta_scripts` (episodic slot
            // model) even for feature films. Route the completion
            // probe through the `/scripts` index so we never hit the
            // retired scalar `/script` endpoint (410).
            const useEpisodicIndex = project.production_type === "tv_series"
                || apiBase === "/beta/projects";
            if (useEpisodicIndex) {
                const { data } = await api.get(
                    `${apiBase}/${projectId}/scripts`,
                );
                const eps = Array.isArray(data) ? data : data?.scripts || [];
                const built = eps.filter((s) => Boolean(s.parsed_at));
                if (built.length === 0) {
                    setCompletion(null);
                    return;
                }
                const scenes = built.reduce(
                    (acc, s) => acc + (s.parsed_scenes_count || 0),
                    0,
                );
                const cast = built.reduce(
                    (acc, s) => acc + (s.parsed_characters_count || 0),
                    0,
                );
                setCompletion({
                    episodesBuilt: built.length,
                    totalEpisodes: eps.length,
                    scenes,
                    cast,
                });
            } else {
                const { data: sp } = await api.get(
                    `${apiBase}/${projectId}/script`,
                );
                if (sp?.parsed_at) {
                    setCompletion({
                        scenes: sp.parsed_scenes_count || 0,
                        cast: sp.parsed_characters_count || 0,
                    });
                } else {
                    setCompletion(null);
                }
            }
        } catch {
            /* swallow */
        }
    }, [projectId, project, apiBase]);

    useEffect(() => {
        if (!project) return;
        refreshCompletion();
        // Iter-155B.4 — once completion lands, stop polling. The
        // user is unlikely to "un-build" scenes & cast from this
        // page; if they hit Build again, the parsed_at timestamp
        // just shifts forward and the existing card still renders.
        if (completion) return;
        const t = setInterval(refreshCompletion, 4000);
        return () => clearInterval(t);
    }, [project, refreshCompletion, completion]);

    const backTo = location.state?.returnTo?.path
        || `${routeBase}/${projectId}`;
    const backLabel = location.state?.returnTo?.label || "Back to project";

    const isBuilt = Boolean(completion);
    const step2Done = isBuilt;

    // Iter-158.13.2.20e.3 — Authority alignment. Under the Production
    // scope (apiBase === "/beta/projects") the canonical Script gate
    // is `can_manage_script` on the caller payload — Owner always;
    // any Production member granted `manage_script`. Under legacy /
    // H&M scopes the gate stays role-based (owner/admin) so existing
    // behaviour is preserved byte-for-byte.
    const canUpload = Boolean(project?.can_manage_script)
        || project?.role === "owner"
        || project?.role === "admin";

    if (!loading && project && !canUpload) {
        return (
            <div className="min-h-screen bg-background pb-12">
                <ProjectHeader projectId={projectId} />
                <main className="max-w-screen-md mx-auto px-4 sm:px-6 pt-4">
                    <Link
                        data-testid="script-setup-back"
                        to={backTo}
                        className="tap inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5"
                    >
                        <ArrowLeft size={14} /> {backLabel}
                    </Link>
                    <div
                        data-testid="script-setup-restricted"
                        className="rounded-xl border border-white/10 bg-card p-6"
                    >
                        <div className="flex items-start gap-3">
                            <FileText size={20} className="text-muted-foreground mt-0.5 shrink-0" />
                            <div>
                                <h1 className="font-display text-2xl font-bold text-foreground">
                                    Script setup is restricted
                                </h1>
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                    The Production Owner and any Production
                                    member granted Manage Script can upload
                                    or build the canonical script. Scenes
                                    and cast will appear across the
                                    Production as soon as they do.
                                </p>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pb-12">
            <ProjectHeader projectId={projectId} />
            <main className="max-w-screen-md mx-auto px-4 sm:px-6 pt-4">
                <Link
                    data-testid="script-setup-back"
                    to={backTo}
                    className="tap inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-5"
                >
                    <ArrowLeft size={14} /> {backLabel}
                </Link>

                <header className="mb-5">
                    <h1
                        data-testid="script-setup-heading"
                        className="font-display text-3xl sm:text-4xl font-bold text-foreground"
                    >
                        Script Setup
                    </h1>
                </header>

                {/* 2-step indicator — minimal, no prose. Step 2
                    flips to a "done" state once parsed_at is set. */}
                <ol
                    data-testid="script-setup-steps"
                    className="mb-6 grid grid-cols-2 gap-3 text-[12px]"
                >
                    <li
                        data-testid="script-setup-step-1"
                        className={`rounded-lg border px-3 py-2.5 flex items-center gap-2 ${
                            isBuilt
                                ? "border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 text-emerald-900 dark:text-emerald-50"
                                : "border-primary/30 bg-primary/5 text-foreground"
                        }`}
                    >
                        <span
                            className={`shrink-0 inline-flex w-6 h-6 rounded-full items-center justify-center font-bold text-[11px] ${
                                isBuilt
                                    ? "bg-emerald-500/30 text-emerald-900 dark:text-emerald-50"
                                    : "bg-primary/20 text-primary"
                            }`}
                        >
                            {isBuilt ? <CheckCircle2 size={12} /> : 1}
                        </span>
                        <span className="font-semibold">Upload script</span>
                        <FileText size={14} className={`ml-auto ${isBuilt ? "text-emerald-700 dark:text-emerald-200" : "text-primary"}`} />
                    </li>
                    <li
                        data-testid="script-setup-step-2"
                        className={`rounded-lg border px-3 py-2.5 flex items-center gap-2 ${
                            step2Done
                                ? "border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 text-emerald-900 dark:text-emerald-50"
                                : "border-white/10 bg-card text-muted-foreground"
                        }`}
                    >
                        <span
                            className={`shrink-0 inline-flex w-6 h-6 rounded-full items-center justify-center font-bold text-[11px] ${
                                step2Done
                                    ? "bg-emerald-500/30 text-emerald-900 dark:text-emerald-50"
                                    : "bg-white/10 text-muted-foreground"
                            }`}
                        >
                            {step2Done ? <CheckCircle2 size={12} /> : 2}
                        </span>
                        <span className="font-semibold">Build Scenes &amp; Cast</span>
                        <Wand2 size={14} className={`ml-auto ${step2Done ? "text-emerald-700 dark:text-emerald-200" : "text-muted-foreground"}`} />
                    </li>
                </ol>

                {error ? (
                    <div
                        data-testid="script-setup-error"
                        className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-[12px] text-red-200 mb-4"
                    >
                        {error}
                    </div>
                ) : null}

                {loading ? (
                    <div
                        data-testid="script-setup-loading"
                        className="h-32 rounded-xl bg-white/5 flex items-center justify-center gap-2 text-sm text-muted-foreground"
                    >
                        <Loader2 size={14} className="animate-spin" />
                        Loading project…
                    </div>
                ) : (project?.production_type === "tv_series"
                    || apiBase === "/beta/projects") ? (
                    /* Iter-158.13.2.20e.3 — Under Production scope
                       every canonical script lives in `beta_scripts`
                       as an episodic slot (single Episode 1 for
                       feature_film, N episodes for tv_series). Route
                       both to EpisodicScriptSetup so the retired
                       scalar `/script` GET is never fired here and no
                       410 banner leaks to the user. */
                    <EpisodicScriptSetup projectId={projectId} />
                ) : (
                    <ScriptSection projectId={projectId} />
                )}

                {/* Iter-155B.4 — post-build completion state.
                    Replaces the previous "Once Build runs, you'll
                    see scenes appear under Scenes…" instructional
                    copy once Build is actually done.
                    Iter-158.13.2.20e.7 — completion telemetry
                    (counts card) stays visible on every scope. The
                    Open Scenes / Open Cast navigation buttons are
                    hidden on Production scope because
                    `/beta/projects/:pid/scenes` and `.../cast` are
                    not registered routes (dept pages live under
                    `/beta/hm-app/projects/*`). See the nav guard
                    below. */}
                {isBuilt ? (
                    <section
                        data-testid="script-setup-completion"
                        className="mt-6 rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-500/50 dark:bg-emerald-500/20 p-4 sm:p-5"
                    >
                        <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-md bg-emerald-500/25 text-emerald-800 dark:text-emerald-100 flex items-center justify-center shrink-0">
                                <CheckCircle2 size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h2
                                    data-testid="script-setup-completion-title"
                                    className="font-display text-lg font-bold text-emerald-900 dark:text-emerald-50"
                                >
                                    Scenes &amp; Cast Ready
                                </h2>
                                <p
                                    data-testid="script-setup-completion-counts"
                                    className="text-[13px] text-emerald-900 dark:text-emerald-50 mt-1"
                                >
                                    <span className="font-semibold">{completion.scenes}</span>{" "}
                                    {completion.scenes === 1 ? "scene" : "scenes"} created ·{" "}
                                    <span className="font-semibold">{completion.cast}</span>{" "}
                                    {completion.cast === 1 ? "cast member" : "cast members"} found
                                    {completion.episodesBuilt != null ? (
                                        <>
                                            {" "}across{" "}
                                            <span className="font-semibold">
                                                {completion.episodesBuilt}
                                            </span>{" "}
                                            {completion.episodesBuilt === 1 ? "episode" : "episodes"}
                                            {completion.totalEpisodes > completion.episodesBuilt
                                                ? ` (${completion.totalEpisodes - completion.episodesBuilt} more to build)`
                                                : ""}
                                        </>
                                    ) : null}
                                </p>
                            </div>
                        </div>
                        {/* Iter-158.13.2.20e.7 — Open Scenes / Open
                            Cast nav is dept-scope only. Hidden on
                            Production scope so tapping doesn't
                            silently dump users on the router's
                            catch-all fallback. */}
                        {isProductionScope ? null : (
                        <div className="mt-4 flex flex-col sm:flex-row gap-2">
                            <button
                                type="button"
                                data-testid="script-setup-completion-open-scenes"
                                onClick={() => navigate(`${routeBase}/${projectId}/scenes`)}
                                className="tap min-tap flex-1 rounded-lg bg-primary hover:bg-primary/90 px-4 py-2.5 text-sm font-semibold text-primary-foreground inline-flex items-center justify-center gap-2"
                            >
                                Open Scenes
                            </button>
                            <button
                                type="button"
                                data-testid="script-setup-completion-open-cast"
                                onClick={() => navigate(`${routeBase}/${projectId}/cast`)}
                                className="tap min-tap flex-1 rounded-lg border border-white/10 bg-card hover:bg-white/5 px-4 py-2.5 text-sm font-medium text-foreground inline-flex items-center justify-center gap-2"
                            >
                                Open Cast
                            </button>
                        </div>
                        )}
                    </section>
                ) : null}

                {/* When the user has clearly finished, hint at the next surface.
                    Iter-157H P1(5) — canonical order is On Set first, Fittings
                    second (CastHub is the source of truth: "On Set primary,
                    leftmost").
                    Iter-158.13.2.20e.7 — On Set / Fittings are dept-scope
                    only (mounted under `/beta/hm-app/projects/*`). Hidden
                    on Production scope to avoid stale navigation. */}
                {isBuilt && !isProductionScope ? (
                    <div className="mt-4 flex flex-col sm:flex-row gap-2">
                        <button
                            type="button"
                            data-testid="script-setup-done-onset"
                            onClick={() => navigate(`${routeBase}/${projectId}/onset`)}
                            className="tap min-tap flex-1 rounded-lg border border-white/10 bg-card hover:bg-white/5 px-4 py-2.5 text-sm font-medium text-foreground"
                        >
                            Open On Set
                        </button>
                        <button
                            type="button"
                            data-testid="script-setup-done-prep"
                            onClick={() => navigate(`${routeBase}/${projectId}/prep`)}
                            className="tap min-tap flex-1 rounded-lg border border-white/10 bg-card hover:bg-white/5 px-4 py-2.5 text-sm font-medium text-foreground"
                        >
                            Open Fittings (Prep)
                        </button>
                    </div>
                ) : null}
            </main>
        </div>
    );
}
